import Timeframe from 'components/Timeframe/Timeframe';
import { DateTime, Duration } from 'luxon';
import { type FC, useMemo } from 'react';
import useSimInventoryStoredFilters from 'state/SimInventory/useSimInventoryStoredFilters';
import type { TimeFrameOption } from 'types/common';
import FilterAccordion, { type FilterProps } from '../../FilterAccordion/FilterAccordion';
import { filterLabels, filterSetToDefault } from '../../shared';
import filterStyles from '../filterStyles.module.css';
import useDateFilter from '../useDateFilter';

const ActivationDateFilter: FC<FilterProps> = ({ onClear, segmentName, form, submit }) => {
  const { watch } = form;
  const storedFilters = useSimInventoryStoredFilters();
  const storedActivationDate = storedFilters?.activationDate;
  const activationDateFilter = watch('activationDate');

  const options: Array<TimeFrameOption> = useMemo(
    () => [
      { value: 'custom', label: 'Custom date range' },
      { value: 'thisMonth', label: 'This month' },
      { value: Duration.fromObject({ days: 1 }), label: 'In the last day' },
      { value: Duration.fromObject({ days: 7 }), label: 'In the last 7 days' },
      { value: Duration.fromObject({ days: 30 }), label: 'In the last 30 days' },
      { value: Duration.fromObject({ days: 90 }), label: 'In the last 90 days' },
      { value: Duration.fromObject({ days: 365 }), label: 'In the last 365 days' },
    ],
    []
  );

  const {
    selectableOptions,
    timeframeProps,
    startFieldProps,
    endFieldProps,
    invalid,
    validationMessage,
  } = useDateFilter({
    options,
    filter: activationDateFilter,
    filterPath: 'activationDate',
    timeframePath: 'activationDate.timeframe',
    startPath: 'activationDate.start',
    endPath: 'activationDate.end',
    submit,
    onClear,
    segmentName,
    form,
    maxDate: DateTime.now().startOf('day'),
  });

  const isSet = useMemo(
    () => !filterSetToDefault('activationDate', storedActivationDate),
    [storedActivationDate]
  );

  return (
    <FilterAccordion
      onClear={onClear}
      label={filterLabels.activationDate}
      isSet={isSet}
      defaultActive={isSet}
    >
      <Timeframe
        id="activationDate"
        options={selectableOptions}
        timeframeProps={timeframeProps}
        startFieldProps={startFieldProps}
        endFieldProps={endFieldProps}
        invalid={invalid}
        validationMessage={validationMessage}
        className={filterStyles.defaultWrapper}
      />
    </FilterAccordion>
  );
};

export default ActivationDateFilter;
